import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { PdfToAudioDemoPage } from '@/components/astro/new-design-25/SEO/pdf-to-audio-demo-page';
import { pdfToAudioSearch } from '@/lib/pdf-to-audio-search';
import { hreflangLinks, seo } from '@/seo/seo';

const heroTitle = 'Spanish PDF to audio';
const heroDescription =
  'Drop a Spanish PDF and hear it read aloud with natural Spain or Latin American voices. Built for academic papers, ebooks, and Spanish-language coursework.';

const featureFeatures = [
  {
    title: 'Spain and Latin American Voices',
    description:
      'Pick metropolitan Spanish or a Latin American variant so the audio matches the source and the audience.',
  },
  {
    title: 'Academic Spanish Vocabulary',
    description:
      'Correct pronunciation of Spanish academic, scientific, and philosophical terminology, not phonetic guesses.',
  },
  {
    title: 'Handles Accented PDFs',
    description:
      'Reads ñ, á, é, í, ó, ú, ü properly even when the PDF uses non-standard font encodings.',
  },
  {
    title: 'Long-Form Friendly',
    description:
      'Designed for full chapters and papers, not just paragraphs, so the audio stays natural at length.',
  },
];

const solutionBenefitsData = {
  title: 'Why Spanish readers stay with our PDF to audio tool',
  items: [
    {
      text: 'Natural Spain and Latin American voices, not a generic Spanish accent tacked onto a default model',
      highlight: 'Spain and Latin American voices',
    },
    {
      text: 'Accurate handling of Spanish academic vocabulary, technical terms, and proper nouns',
      highlight: 'academic vocabulary',
    },
    {
      text: 'Smart citation handling so reference numbers and inline cites do not break the flow',
      highlight: 'Smart citation handling',
    },
    {
      text: '24-hour read-along links with PDF view, audio, speed controls, and a playbar',
      highlight: '24-hour read-along links',
    },
  ],
};

const useCasesData = {
  title: 'How Spanish PDF to audio gets used',
  description:
    'From Spanish university students to translators and Hispanic researchers, here is who reaches for this tool.',
  useCases: [
    {
      title: 'University Student in Spain or Latin America',
      challenge:
        'Get through dense Spanish course readings without burning out the eyes between lectures and lab work.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop course PDFs, listen on the way to class with native Spanish, adjustable speed for tough sections.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Robotic Spanish on academic vocabulary makes a 30-page reading sound like noise after 10 minutes.',
        },
      ],
    },
    {
      title: 'Hispanic Researcher Triaging Literature',
      challenge:
        'Filter dozens of Spanish-language papers per week to find the ones worth a deeper read.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Run papers at 1.8–2x for filtering, slow back down on methods and results sections.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'No reliable speed control, no proper voice, no way to listen while doing other work.',
        },
      ],
    },
    {
      title: 'Spanish Language Learner',
      challenge:
        'Move past textbook audio and into real Spanish PDFs at a pace you can actually follow.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop any Spanish PDF, slow to 0.7x, follow along with synced highlighting in the Listening app.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Wooden phrasing trains the wrong rhythm and slows down real comprehension later.',
        },
      ],
    },
    {
      title: 'Spanish-to-English Translator',
      challenge:
        'Review long Spanish source documents efficiently without rereading every line from scratch.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Listen while reading, audio handles the words so eyes can focus on meaning and translation flow.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Mispronunciation on academic vocabulary introduces ambiguity that slows the work down.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Why Spanish PDFs Need a Real Spanish Voice',
    content:
      'Spanish academic and professional PDFs are dense with regional vocabulary, accented characters, and technical terms drawn from Latin and Greek roots. Generic English-language PDF readers force Spanish through an English phonetic engine and the result is unusable. The accent marks are dropped, the rhythm is flat, and academic terms come out garbled.',
    contentSecondary:
      'Listening uses native Spanish voices trained on academic and journalistic Spanish content. The voices handle both metropolitan and Latin American variants with correct pronunciation of accented vowels, conjugated verbs, and technical vocabulary. The result is audio you can leave running for an hour without your ear fighting the voice.',
  },
  {
    title: 'Spanish PDF to Audio for Study, Research, and Daily Reading',
    content:
      'For Hispanic students and researchers, listening turns commutes, walks, and household tasks into reading time. Drop any Spanish PDF into the converter on this page and the audio is ready in seconds. Adjustable speed lets you skim or slow down on tough passages. The Listening app extends the same voices across the iOS, Android, and Chrome extension surfaces.',
    contentSecondary:
      'For Spanish learners outside the Spanish-speaking world, this is the easiest way to get real native Spanish input from any text source. Pair the audio with synced highlighting in the Listening app and you can read and listen at the same time, building both written and listening fluency.',
  },
];

const faqs = [
  {
    question: 'Which Spanish voices are available?',
    answer:
      'Listening offers natural Spanish voices in male and female options across Spain and Latin American variants. Pick the accent that fits your audience.',
  },
  {
    question: 'Does it handle accented characters and special Spanish letters?',
    answer:
      'Yes. Accented vowels (á, é, í, ó, ú, ü) and the letter ñ are recognized and pronounced naturally, even when the PDF uses non-standard font encodings.',
  },
  {
    question: 'Can I use it for Spanish academic content?',
    answer:
      'Yes. The voices are trained on Spanish academic, literary, and journalistic content. Scientific terminology and formal Spanish vocabulary pronounce correctly.',
  },
  {
    question: 'What are the free preview limits?',
    answer:
      'The free preview is capped at 10 pages, 25,000 characters, and 25 MB per PDF. Sign up to convert full Spanish documents with no caps.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/spanish-pdf-to-audio')({
  validateSearch: pdfToAudioSearch,
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Spanish PDF to Audio — Listen to Spanish PDFs with Native Voices | Listening',
        description:
          'Convert Spanish PDFs to audio with natural Spain and Latin American voices. Built for academic papers, ebooks, and Spanish-language coursework. Free, no sign-up.',
      }),
    ],
    links: [...hreflangLinks('/spanish-pdf-to-audio')],
  }),
  component: () => (
    <PdfToAudioDemoPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription='Quick answers about converting Spanish PDFs to audio.'
      faqTitle='Spanish PDF to audio FAQ'
      faqs={faqs}
      featureBadge='Spanish voices'
      featureFeatures={featureFeatures}
      featureTitle='the Spanish PDF tool serious readers stay with:'
      preferredLang='es'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
